/**
 * AI transcription seats: plan pricing, the practice's seat subscription, seat assignment and
 * per-period usage.
 *
 * Mirrors /api/transcription-seats on the REST API. No Stripe Price ID or monetary figure is ever
 * calculated here - every amount shown to a practice comes back from the server, which reads it
 * from Stripe, because proration cannot be reproduced client side.
 */
app.service("TranscriptionSeatsAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		const baseUri = ENV.API + "/api/transcription-seats";

		const SECONDS_PER_MINUTE = 60;
		const MINUTES_PER_HOUR = 60;

		/**
		 * Generates the common headers required for API authentication.
		 * @returns {Object} The headers object.
		 */
		const getHeaders = function () {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${Session.practice.id}`,
			};
		};

		/**
		 * Headers for the admin console, which acts on a chosen practice rather than the
		 * logged-in admin's own session practice.
		 * @param {number} practiceId - The practice being administered.
		 * @returns {Object} The headers object.
		 */
		const getAdminHeaders = function (practiceId) {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${practiceId}`,
			};
		};

		var service = {
			isProcessing: false,

			/** The seat tiers, matching TranscriptionSeatTierType on the server. */
			TIER: {
				FREE_TRIAL: "FREE_TRIAL",
				STANDARD: "STANDARD",
				PREMIUM: "PREMIUM",
			},

			/** Statuses where the practice still holds a usable subscription. */
			LIVE_STATUSES: ["TRIALING", "ACTIVE", "PAST_DUE"],

			// =================================================================
			// Plans & Subscription
			// =================================================================

			/**
			 * Retrieves the purchasable seat tiers and their prices.
			 */
			getSeatPlans: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/plans`,
					headers: getHeaders(),
				});
			},

			/**
			 * Retrieves the practice's current seat subscription, including owned, assigned and
			 * pending counts per tier.
			 */
			getSubscription: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/subscription`,
					headers: getHeaders(),
				});
			},

			/**
			 * Dry runs a seat quantity change. Returns the recurring monthly total, the prorated
			 * amount falling due immediately, and any tier that would strand an assigned user.
			 * @param {Array} seats - [{ tier, quantity }]
			 */
			previewSubscription: function (seats) {
				return $http({
					method: "POST",
					url: `${baseUri}/subscription/preview`,
					headers: getHeaders(),
					data: { seats: seats },
				});
			},

			/**
			 * Creates the seat subscription, or changes its quantities.
			 *
			 * Increases are charged immediately with proration; reductions are queued for the period
			 * boundary. The request ID must be held across a retry, so a timeout cannot charge twice.
			 * @param {Array} seats - [{ tier, quantity }]
			 * @param {string} requestId - Client generated idempotency key.
			 */
			saveSubscription: function (seats, requestId) {
				return $http({
					method: "POST",
					url: `${baseUri}/subscription`,
					headers: getHeaders(),
					data: { seats: seats, request_id: requestId },
				});
			},

			/**
			 * Cancels the seat subscription at the end of the current billing period.
			 */
			cancelSubscription: function () {
				return $http({
					method: "DELETE",
					url: `${baseUri}/subscription`,
					headers: getHeaders(),
				});
			},

			/**
			 * Clears a pending cancellation, so the subscription renews as normal.
			 *
			 * Kept separate from saveSubscription so a practice can keep its seats without also having to
			 * change how many it holds. Returns the refreshed subscription.
			 */
			resumeSubscription: function () {
				return $http({
					method: "POST",
					url: `${baseUri}/subscription/resume`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Admin console: invoice billed (enterprise) subscriptions
			// =================================================================

			/**
			 * Retrieves a practice's seat subscription for the admin console. ADMIN role only.
			 * @param {number} practiceId - The practice being administered.
			 */
			getInvoiceSubscription: function (practiceId) {
				return $http({
					method: "GET",
					url: `${baseUri}/subscription/invoice`,
					headers: getAdminHeaders(practiceId),
				});
			},

			/**
			 * Grants a practice invoiced seats for a paid term, without Stripe. ADMIN role only.
			 *
			 * Covers enterprise agreements and customers who have paid up front by invoice.
			 * @param {number} practiceId - The practice being administered.
			 * @param {Array} seats - [{ tier, quantity }]
			 * @param {string} paidUntilUtc - The end of the paid term, ISO UTC.
			 */
			saveInvoiceSubscription: function (practiceId, seats, paidUntilUtc) {
				return $http({
					method: "PUT",
					url: `${baseUri}/subscription/invoice`,
					headers: getAdminHeaders(practiceId),
					data: { seats: seats, paid_until_utc: paidUntilUtc },
				});
			},

			/**
			 * Revokes a practice's invoiced seats immediately, releasing every seat. ADMIN role only.
			 * @param {number} practiceId - The practice being administered.
			 */
			revokeInvoiceSubscription: function (practiceId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/subscription/invoice`,
					headers: getAdminHeaders(practiceId),
				});
			},

			// =================================================================
			// Assignment & Usage
			// =================================================================

			/**
			 * Retrieves seat usage for a billing period, with a row for every user in the practice.
			 * @param {string} [periodStartUtc] - Omit for the period currently in progress.
			 */
			getSeatUsage: function (periodStartUtc) {
				return $http({
					method: "GET",
					url: periodStartUtc ? `${baseUri}/usage?periodStartUtc=${encodeURIComponent(periodStartUtc)}` : `${baseUri}/usage`,
					headers: getHeaders(),
				});
			},

			/**
			 * Retrieves the billing periods that can be reported on, most recent first.
			 */
			getSeatUsagePeriods: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/usage/periods`,
					headers: getHeaders(),
				});
			},

			/**
			 * Applies a batch of seat assignments and returns the authoritative usage state.
			 *
			 * Batched rather than per-user because the assigned count may never exceed the purchased
			 * count, and two users swapping tiers one at a time would be rejected halfway through.
			 * @param {Array} assignments - [{ user_id, tier }]. A null tier releases the seat.
			 */
			updateSeatAssignments: function (assignments) {
				return $http({
					method: "PUT",
					url: `${baseUri}/assignments`,
					headers: getHeaders(),
					data: { assignments: assignments },
				});
			},

			/**
			 * Retrieves the logged-in user's own entitlement.
			 */
			getMyEntitlement: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/me`,
					headers: getHeaders(),
				});
			},

			// =================================================================
			// Shared Presentation Helpers
			// =================================================================

			/**
			 * Display names for the tiers, so no screen ever renders a raw enum value.
			 */
			TIER_LABELS: {
				FREE_TRIAL: "Free trial",
				STANDARD: "Standard",
				PREMIUM: "Premium",
			},

			/**
			 * @param {string} [tier] the tier, or null for a user holding no seat at all.
			 */
			getTierLabel: function (tier) {
				if (!tier) {
					return "No seat";
				}

				return service.TIER_LABELS[tier] || tier;
			},

			isTrialTier: function (tier) {
				return tier === service.TIER.FREE_TRIAL;
			},

			/**
			 * Whether the practice holds a subscription it can still change seats on.
			 */
			isSubscriptionLive: function (subscription) {
				return !!subscription && service.LIVE_STATUSES.indexOf(subscription.status) !== -1;
			},

			/**
			 * Reads one tier's allocation out of a subscription response.
			 * @returns {Object} Always an object, so callers never null check a count.
			 */
			getAllocation: function (subscription, tier) {
				const empty = { tier: tier, quantity_owned: 0, quantity_assigned: 0, quantity_pending: null };

				if (!subscription || !subscription.seats) {
					return empty;
				}

				const allocation = subscription.seats.filter(function (seat) {
					return seat.tier === tier;
				})[0];

				if (!allocation) {
					return empty;
				}

				return {
					tier: tier,
					quantity_owned: allocation.quantity_owned || 0,
					quantity_assigned: allocation.quantity_assigned || 0,
					quantity_pending: allocation.quantity_pending == null ? null : allocation.quantity_pending,
				};
			},

			/**
			 * Formats a duration for display, e.g. "2h 05m" or "45m".
			 */
			formatSeconds: function (seconds) {
				const totalMinutes = Math.round((seconds || 0) / SECONDS_PER_MINUTE);
				const hours = Math.floor(totalMinutes / 60);
				const minutes = totalMinutes % 60;

				if (hours <= 0) {
					return minutes + "m";
				}

				return hours + "h " + (minutes < 10 ? "0" : "") + minutes + "m";
			},

			/**
			 * Converts seconds to whole minutes, for the compact table columns.
			 */
			toMinutes: function (seconds) {
				return Math.round((seconds || 0) / SECONDS_PER_MINUTE);
			},

			/**
			 * Renders a seat's monthly allowance in hours, which is the unit the tiers are sold in.
			 * The server reports it in minutes, so a tier that is not a whole number of hours still
			 * has to read sensibly.
			 */
			formatAllowanceHours: function (minutes) {
				const total = minutes || 0;
				const hours = Math.floor(total / MINUTES_PER_HOUR);
				const remainder = total % MINUTES_PER_HOUR;

				if (hours > 0 && remainder === 0) {
					return hours + (hours === 1 ? " hour" : " hours");
				}

				if (hours === 0) {
					return remainder + (remainder === 1 ? " minute" : " minutes");
				}

				return hours + "h " + (remainder < 10 ? "0" : "") + remainder + "m";
			},

			/**
			 * Converts the smallest currency unit the server bills in to whole units for display.
			 */
			fromCents: function (cents) {
				return (cents || 0) / 100;
			},

			/**
			 * A client generated idempotency key, held across a retry so a timeout cannot charge twice.
			 */
			newRequestId: function () {
				if (window.crypto && typeof window.crypto.randomUUID === "function") {
					return window.crypto.randomUUID();
				}

				return "seat-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
			},

			/**
			 * Pulls a server side message out of a failed $http response, falling back to a default.
			 */
			getErrorMessage: function (error, fallback) {
				const data = error && error.data ? error.data : null;
				const message = data ? data.message || data.error : null;

				if (!message || typeof message !== "string" || service.isInternalErrorMessage(message)) {
					return fallback;
				}

				return message;
			},

			/**
			 * Whether a server message is an internal failure rather than something written for a
			 * practice to read. Those arrive as "Internal error while processing your request:
			 * [details: ...]" and carry Stripe customer and request ids, which mean nothing on this
			 * screen and should not be put in front of anyone.
			 */
			isInternalErrorMessage: function (message) {
				return message.indexOf("[details:") !== -1 || message.indexOf("Internal error") === 0;
			},
		};

		return service;
	},
]);
